import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { typeMeta } from "../../lib/accountTypes";
import AccountPicker, { DuplicateWarning } from "../../components/AccountPicker";
import {
    TreeStructure, Plus, CircleNotch, LinkSimple, LinkBreak, Phone, EnvelopeSimple,
    GitMerge, X, WarningCircle,
} from "@phosphor-icons/react";

const STAGE_LABELS = {
    lead: "Lead", contacted: "Contacted", proposal: "Proposal", negotiation: "Negotiation",
    won: "Supplier list", active: "Active", closed_lost: "Lost",
};

function ago(iso) {
    if (!iso) return "No activity yet";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days <= 0) return "Active today";
    if (days === 1) return "Last activity yesterday";
    return `Last activity ${days}d ago`;
}

const EMPTY = { name: "", contact: "", email: "", phone: "" };

// ── Child accounts: property managers / sites / branches ───────────────
export function FamilyCard({ client, kids, onChanged, navigate }) {
    const isCustomer = (client.type || "customer") === "customer";
    const title = isCustomer ? "Property managers & locations" : "Branches & offices";
    const [mode, setMode] = useState(null); // "new" | "link" | null
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    async function addContactTo(accountId) {
        const body = { name: form.contact.trim(), email: form.email.trim(), phone: form.phone.trim(),
            role: isCustomer ? "property_manager" : "", is_primary: true };
        try { await endpoints.addClientContact(accountId, body); }
        catch (e) {
            const d = e?.response?.data?.detail;
            if (e?.response?.status === 409 && d?.matches) {
                if (window.confirm(`${d.message}.\n\nAdd them to this account as well?`)) {
                    await endpoints.addClientContact(accountId, { ...body, force: true });
                }
            } else throw e;
        }
    }

    async function createChild() {
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            const id = crypto.randomUUID();
            await endpoints.saveClient({
                id, name: form.name.trim(), type: client.type || "customer", parent_id: client.id,
                stage: client.stage || "lead", owner: client.owner || "", owner_id: client.owner_id || "",
                address: "", contact_name: "", email: "", phone: "",
            });
            if (form.contact.trim()) await addContactTo(id);
            setForm(EMPTY);
            setMode(null);
            onChanged();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function link(accountId) {
        try { await endpoints.patchClient(accountId, { parent_id: client.id }); setMode(null); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }
    async function unlink(kid) {
        if (!window.confirm(`Take ${kid.name} out from under ${client.name}? It stays as its own account.`)) return;
        try { await endpoints.patchClient(kid.id, { parent_id: null }); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="rom-card" data-testid="family-card">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-1.5"><TreeStructure size={14} weight="bold" /> {title} <span className="text-[var(--rom-text-2)] font-normal ml-1">({kids.length})</span></h3>
                <div className="flex items-center gap-1">
                    <button onClick={() => setMode(mode === "link" ? null : "link")} className="btn btn-ghost btn-sm" data-testid="family-link-toggle"><LinkSimple size={12} weight="bold" /> Link existing</button>
                    <button onClick={() => setMode(mode === "new" ? null : "new")} className="btn btn-ghost btn-sm" data-testid="family-add-toggle"><Plus size={12} weight="bold" /> Add</button>
                </div>
            </div>
            <div className="rom-card-body space-y-2">
                {mode === "link" && (
                    <div className="p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px] space-y-1.5" data-testid="family-link-form">
                        <label className="label">Move an existing account under {client.name}</label>
                        <AccountPicker excludeId={client.id} onChange={(id) => id && link(id)} testId="family-link-picker" />
                    </div>
                )}
                {mode === "new" && (
                    <div className="p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px] space-y-2" data-testid="family-add-form">
                        <input className="input" placeholder={isCustomer ? "Property or portfolio (e.g. Riverside Apartments)" : "Branch / office name"}
                            value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="family-name-input" />
                        <div className="grid grid-cols-3 gap-2">
                            <input className="input" placeholder={isCustomer ? "Property manager" : "Main contact"} value={form.contact} onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} data-testid="family-contact-input" />
                            <input className="input" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="family-email-input" />
                            <input className="input" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                        </div>
                        <DuplicateWarning name={form.name} excludeId={client.id} />
                        <p className="text-[11px] text-[var(--rom-text-3)]">Company-level details (MSA, insurers, billing) stay on {client.name} — nothing is copied, so there's one record for each.</p>
                        <button className="btn btn-primary btn-sm" onClick={createChild} disabled={busy || !form.name.trim()} data-testid="family-save-btn">
                            {busy ? <CircleNotch size={13} className="animate-spin" /> : "Save"}
                        </button>
                    </div>
                )}
                {kids.length === 0 && !mode && (
                    <div className="text-sm text-[var(--rom-text-3)] text-center py-4">
                        {isCustomer ? "No property managers or sites under this account yet." : "No branches under this account yet."}
                    </div>
                )}
                {kids.map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-2 p-2.5 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)]" data-testid={`family-row-${k.id}`}>
                        <button onClick={() => navigate(`/crm/accounts/${k.id}`)} className="min-w-0 text-left flex-1">
                            <div className="font-semibold text-sm truncate">{k.name}</div>
                            <div className="text-xs text-[var(--rom-text-2)] flex items-center gap-3 mt-0.5 flex-wrap">
                                {k.primary_contact && <span>{k.primary_contact}</span>}
                                {k.phone && <span className="flex items-center gap-1"><Phone size={11} /> {k.phone}</span>}
                                {k.email && <span className="flex items-center gap-1"><EnvelopeSimple size={11} /> {k.email}</span>}
                            </div>
                            <div className="text-[10px] text-[var(--rom-text-3)] mt-0.5">{ago(k.last_activity_at)}{k.owner ? ` · ${k.owner}` : ""}</div>
                        </button>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="chip chip-gray text-[10px]">{STAGE_LABELS[k.stage] || k.stage}</span>
                            <button onClick={() => unlink(k)} className="text-[var(--rom-text-3)] hover:text-red-600" title="Take out of this group" data-testid={`family-unlink-${k.id}`}><LinkBreak size={13} weight="bold" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Merge a duplicate account into this one ────────────────────────────
export function MergeButton({ client, onMerged }) {
    const [open, setOpen] = useState(false);
    const [suggested, setSuggested] = useState([]);
    const [sourceId, setSourceId] = useState(null);
    const [sourceName, setSourceName] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!open) return;
        endpoints.clientDuplicates().then(({ data }) => {
            const mine = (data || []).filter((p) => p.a.id === client.id || p.b.id === client.id)
                .map((p) => ({ ...(p.a.id === client.id ? p.b : p.a), reasons: p.reasons }));
            setSuggested(mine);
        }).catch(() => setSuggested([]));
    }, [open, client.id]);

    function close() { setOpen(false); setSourceId(null); setSourceName(""); setErr(""); }

    async function merge() {
        if (!sourceId) return;
        setBusy(true); setErr("");
        try { await endpoints.mergeClient(client.id, sourceId); close(); onMerged(); }
        catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <>
            <button onClick={() => setOpen(true)} className="btn btn-ghost" title="Merge a duplicate into this account" data-testid="merge-open-btn">
                <GitMerge size={14} weight="bold" /> Merge duplicate
            </button>
            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="merge-modal">
                    <div className="rom-card w-full max-w-lg">
                        <div className="rom-card-head">
                            <h3 className="flex items-center gap-1.5"><GitMerge size={14} weight="bold" /> Merge into {client.name}</h3>
                            <button onClick={close}><X size={18} /></button>
                        </div>
                        <div className="rom-card-body space-y-3">
                            {suggested.length > 0 && (
                                <div className="space-y-1.5">
                                    <div className="label">Likely duplicates</div>
                                    {suggested.map((s) => (
                                        <button key={s.id} onClick={() => { setSourceId(s.id); setSourceName(s.name); }}
                                            className={`w-full text-left p-2.5 border rounded-[4px] text-sm ${sourceId === s.id ? "border-[var(--rom-navy)] bg-slate-50" : "border-[var(--rom-border)]"}`}
                                            data-testid={`merge-suggestion-${s.id}`}>
                                            <div className="font-semibold">{s.name}</div>
                                            <div className="text-[11px] text-[var(--rom-text-3)]">{typeMeta(s.type).label} · {s.reasons.join(" · ")}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div>
                                <div className="label">{suggested.length ? "Or pick another account" : "Pick the duplicate account"}</div>
                                <AccountPicker value={sourceId} excludeId={client.id} onChange={(id, a) => { setSourceId(id); setSourceName(a?.name || ""); }} testId="merge-picker" />
                            </div>
                            {sourceId && (
                                <div className="text-xs p-3 rounded-[6px] bg-amber-50 border border-amber-300 flex gap-2" data-testid="merge-confirm-text">
                                    <WarningCircle size={14} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
                                    <span><b>{sourceName || "That account"}</b> will be deleted. Its contacts, notes, activity, follow-ups, jobs, projects, invoices, contracts and any accounts under it move to <b>{client.name}</b>. This can't be undone.</span>
                                </div>
                            )}
                            {err && <div className="text-xs text-red-600">{err}</div>}
                            <div className="flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={close}>Cancel</button>
                                <button className="btn btn-primary" onClick={merge} disabled={!sourceId || busy} data-testid="merge-confirm-btn">
                                    {busy ? <CircleNotch size={14} className="animate-spin" /> : "Merge"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
